import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Edit, ArrowLeft, Download, Clock, Tag, CheckCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import TemplatePreview from './TemplatePreview';
import { formatDate } from '../../utils/formatters';
import { apiClient } from '../../api/client';

interface Zone {
  id: string;
  name: string;
  type: 'text' | 'table' | 'checkbox' | 'date';
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  rules: any[];
  required: boolean;
}

interface Template {
  id: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'archived';
  lastUsed: string;
  version: string;
  createdAt: string;
  documentTypes: string[];
  zones: Zone[];
  rules: any[];
}

const TemplateViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'preview'>('overview');

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/api/templates/${id}`);
      setTemplate(response.data);
    } catch (error: unknown) {
      let errorMessage = 'Failed to fetch template';
      
      if (error && typeof error === 'object') {
        if ('response' in error && error.response && typeof error.response === 'object') {
          const axiosError = error as { response: { data?: { detail?: string } } };
          errorMessage = axiosError.response.data?.detail || errorMessage;
        } else if ('message' in error && typeof error.message === 'string') {
          errorMessage = error.message;
        }
      }
      
      console.error('Error fetching template:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleExportTemplate = () => {
    if (!template) return;
    
    const data = JSON.stringify(template, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `template-${template.name.toLowerCase().replace(/\s+/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-red-300 mb-4">{error}</p>
        <button
          onClick={() => navigate('/templates')}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-lg text-purple-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Templates
        </button>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <p className="text-purple-300">Template not found</p>
        <button
          onClick={() => navigate('/templates')}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-lg text-purple-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Templates
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/templates')}
            className="p-2 hover:bg-purple-500/20 rounded-lg text-purple-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{template.name}</h1>
            <p className="text-sm text-purple-400/60">{template.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/templates/${id}/edit`)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300"
          >
            <Edit className="w-4 h-4" />
            Edit Template
          </button>
          <button
            onClick={handleExportTemplate}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg text-purple-300"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-purple-500/20 mb-6">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-3 text-sm font-medium ${
            activeTab === 'overview' 
              ? 'text-purple-300 border-b-2 border-purple-500' 
              : 'text-purple-400/60 hover:text-purple-300'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('preview')}
          className={`px-4 py-3 text-sm font-medium ${
            activeTab === 'preview' 
              ? 'text-purple-300 border-b-2 border-purple-500' 
              : 'text-purple-400/60 hover:text-purple-300'
          }`}
        >
          Preview & Test
        </button>
      </div>

      {/* Content */}
      {activeTab === 'overview' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Template Info */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-lg">Template Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Status</label>
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs 
                    ${template.status === 'active' 
                      ? 'bg-green-500/20 text-green-300' 
                      : template.status === 'draft'
                      ? 'bg-yellow-500/20 text-yellow-300'
                      : 'bg-purple-500/20 text-purple-300'
                    }`}>
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {template.status}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Version</label>
                  <p className="text-purple-100">{template.version}</p>
                </div>
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Created</label>
                  <p className="text-purple-100">{formatDate(template.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Last Used</label>
                  <p className="text-purple-100">{formatDate(template.lastUsed)}</p>
                </div>
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Document Types</label>
                  <div className="flex flex-wrap gap-2">
                    {template.documentTypes?.map(type => (
                      <span 
                        key={type}
                        className="px-2 py-1 text-xs rounded-full bg-purple-500/10 text-purple-300"
                      >
                        {type}
                      </span>
                    )) || <span className="text-purple-400/60">No document types specified</span>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Total Zones</label>
                  <p className="text-2xl font-bold text-purple-100">{template.zones?.length || 0}</p>
                </div>
                <div>
                  <label className="block text-sm text-purple-400 mb-1">Extraction Rules</label>
                  <p className="text-2xl font-bold text-purple-100">{template.rules?.length || 0}</p>
                </div>
                {/* Add more stats as needed */}
              </div>
            </CardContent>
          </Card>

          {/* Zones List */}
          <Card className="md:col-span-3">
            <CardHeader>
              <CardTitle className="text-lg">Extraction Zones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {template.zones?.length > 0 ? (
                  template.zones.map((zone) => (
                    <div key={zone.id} className="p-4 bg-purple-500/5 rounded-lg">
                      <h3 className="font-medium text-purple-100 mb-2">{zone.name}</h3>
                      <p className="text-sm text-purple-400/60">Type: {zone.type}</p>
                      <p className="text-sm text-purple-400/60">
                        Rules: {zone.rules?.length || 0}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="md:col-span-3 p-4 text-center text-purple-400/60">
                    No zones defined for this template yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      ) : (
        <TemplatePreview zones={template.zones || []} />
      )}
    </div>
  );
};

export default TemplateViewer;